import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { MdOutlineInsertComment } from "react-icons/md";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import { NAME_SPACES } from "shared/locales/constants";
import { SUBSCRIPTION_FORM_KEYS } from "utils/constants";
import { isAcorisNonResponsiblePlan } from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/insurancePlan";

const CLAUSE_FALLBACKS = {
  DOCUMENTS_RECEIVED:
    "L'adhérent(e) reconnaît avoir reçu, préalablement à son adhésion, les Statuts, le Règlement Intérieur, le Règlement Mutualiste, le Document d'Information sur le Produit d'Assurance (IPID), la notice d'information ainsi que la notice d'assistance et la procédure RGPD. L'adhérent(e) déclare en avoir pris connaissance et en accepter les dispositions.",
  RGPD_EXECUTION:
    "L'adhérent(e) accepte que ses données personnelles et ses données relatives à sa santé nécessaires à la mise en œuvre et au suivi de son parcours d'accompagnement santé soient collectées et traitées par ACORIS Mutuelles dans le cadre de l'exécution de son contrat santé.",
  ELECTRONIC_SIGNATURE:
    "L'adhérent(e) est informé(e) que la communication des documents contractuels ainsi que les informations relatives à l'exécution de son contrat d'assurance (communication, avis d'échéance, etc.) est réalisée de manière électronique. L'adhérent(e) atteste que l'adresse e-mail transmise est valide et lui permet de recevoir ces informations.",
  NON_RESPONSIBLE:
    "L'adhérent(e) certifie avoir eu connaissance de l'existence d'une version responsable de cette gamme et fait explicitement le choix de souscrire à la version non responsable du contrat.",
};

const Information = ({ bindField, initialValues, setPercent }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const INFORMATION = t(
    "SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.INFORMATION",
    {
      returnObjects: true,
    },
  );
  const CLAUSES = INFORMATION.CLAUSES || {};
  const showNonResponsibleClause = isAcorisNonResponsiblePlan(
    get(initialValues, "insurancePlan"),
  );

  const documentsReceivedPath = "project.fields.acceptDocumentsReceived";
  const documentsReceivedLabel =
    CLAUSES.DOCUMENTS_RECEIVED || CLAUSE_FALLBACKS.DOCUMENTS_RECEIVED;
  const documentsReceivedField = bindField(documentsReceivedPath, {
    label: documentsReceivedLabel,
    required: true,
    onChange: () => {
      setPercent(initialValues);
    },
  });

  const acceptAgreementPath = "project.fields.acceptAgreement";
  const acceptAgreementLabel =
    CLAUSES.RGPD_EXECUTION || CLAUSE_FALLBACKS.RGPD_EXECUTION;
  const acceptAgreementField = bindField(acceptAgreementPath, {
    label: acceptAgreementLabel,
    required: true,
    onChange: () => {
      setPercent(initialValues);
    },
  });

  const acceptFeesPath = "project.fields.acceptFees";
  const acceptFeesLabel =
    CLAUSES.ELECTRONIC_SIGNATURE || CLAUSE_FALLBACKS.ELECTRONIC_SIGNATURE;
  const acceptFeesField = bindField(acceptFeesPath, {
    label: acceptFeesLabel,
    required: true,
    onChange: () => {
      setPercent(initialValues);
    },
  });

  const acceptProductStatusesPath = "project.fields.acceptProductStatuses";
  const nonResponsibleLabel =
    CLAUSES.NON_RESPONSIBLE || CLAUSE_FALLBACKS.NON_RESPONSIBLE;
  const acceptProductStatusesField = bindField(acceptProductStatusesPath, {
    label: nonResponsibleLabel,
    required: true,
    onChange: () => {
      setPercent(initialValues);
    },
  });

  const specificNeedsPath = "project.fields.specificNeeds";
  const specificNeedsLabel =
    INFORMATION.SPECIFIC_NEEDS?.LABEL || "Besoins spécifiques";
  const specificNeedsField = bindField(specificNeedsPath, {
    label: specificNeedsLabel,
    onChange: () => {
      setPercent(initialValues);
    },
    onBlur: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.INFORMATION}-section`}
    >
      <Form
        type="vertical"
        title={
          INFORMATION.ACORIS_TITLE || INFORMATION.LMF_TITLE || INFORMATION.TITLE
        }
      >
        <div className="grid--1">
          <FormField
            path={documentsReceivedField.path}
            label={documentsReceivedField.label}
            required={documentsReceivedField.required}
            disabled={documentsReceivedField.disabled}
          >
            {(field) => (
              <Input
                type="switch"
                label={documentsReceivedLabel}
                checked={get(initialValues, documentsReceivedPath) === true}
                {...field}
                onChange={documentsReceivedField.onChange}
                onBlur={documentsReceivedField.onBlur}
              />
            )}
          </FormField>
          <FormField
            path={acceptAgreementField.path}
            label={acceptAgreementField.label}
            required={acceptAgreementField.required}
            disabled={acceptAgreementField.disabled}
          >
            {(field) => (
              <Input
                type="switch"
                label={acceptAgreementLabel}
                checked={get(initialValues, acceptAgreementPath) === true}
                {...field}
                onChange={acceptAgreementField.onChange}
                onBlur={acceptAgreementField.onBlur}
              />
            )}
          </FormField>
          <FormField
            path={acceptFeesField.path}
            label={acceptFeesField.label}
            required={acceptFeesField.required}
            disabled={acceptFeesField.disabled}
          >
            {(field) => (
              <Input
                type="switch"
                label={acceptFeesLabel}
                checked={get(initialValues, acceptFeesPath) === true}
                {...field}
                onChange={acceptFeesField.onChange}
                onBlur={acceptFeesField.onBlur}
              />
            )}
          </FormField>
          {showNonResponsibleClause && (
            <FormField
              path={acceptProductStatusesField.path}
              label={acceptProductStatusesField.label}
              required={acceptProductStatusesField.required}
              disabled={acceptProductStatusesField.disabled}
            >
              {(field) => (
                <Input
                  type="switch"
                  label={nonResponsibleLabel}
                  checked={
                    get(initialValues, acceptProductStatusesPath) === true
                  }
                  {...field}
                  onChange={acceptProductStatusesField.onChange}
                  onBlur={acceptProductStatusesField.onBlur}
                />
              )}
            </FormField>
          )}
          <FormField
            path={specificNeedsField.path}
            label={specificNeedsField.label}
            required={specificNeedsField.required}
            disabled={specificNeedsField.disabled}
          >
            {(field) => (
              <Input
                type="textarea"
                label={specificNeedsLabel}
                placeholder={
                  INFORMATION.SPECIFIC_NEEDS?.PLACEHOLDER ||
                  "Saisir les besoins spécifiques du prospect"
                }
                icon={<MdOutlineInsertComment size={16} />}
                defaultValue={specificNeedsField.defaultValue}
                {...field}
                onChange={specificNeedsField.onChange}
                onBlur={specificNeedsField.onBlur}
                autoSize={{ minRows: 2, maxRows: 10 }}
                optional
              />
            )}
          </FormField>
        </div>
      </Form>
    </div>
  );
};

export default Information;
